import { LinkOutlined, TeamOutlined } from '@ant-design/icons';
import { landmineNames } from '@mm/constants/landmine-names';
import { useWhitelabelConfig } from '@mm/hooks';
import { WebhookIcon } from '@mm/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { FormattedConnectionModel } from '@mm/state/model/ConnectionModel';
import { selectJSONWebhooksConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { selectCrmsLeadsSent } from '@mm/state/selectors/connectionsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { ConfigProvider, Table } from 'antd';
import { ColumnType } from 'antd/es/table/interface';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { autoIncrementedName } from '../../../../../common/helpers/name-autoincrement';
import { BaseIntegration } from '../../../beta/modules/Integrations/BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../beta/modules/Integrations/BaseIntegration/BaseIntegrationHeader';
import { ReactComponent as AddWebhookIcon } from '../../../beta/modules/Integrations/icons/add-webhook.svg';
import { Help } from '../../../beta/modules/Integrations/NativeIntegrations/components/KlaviyoIntegrations/KlaviyoHelpLink';
import './ConnectionsPanel.pcss';

type Props = {
  cellData: ColumnType<FormattedConnectionModel>[];
};

export const UNTITLED_CONNECTION_NAME = 'Untitled connection';

export const ConnectionsPanel = (props: Props) => {
  const { cellData } = props;
  const connections = useSelector(selectJSONWebhooksConnections);
  const connectedPageId = useSelector(activeFacebookPageIdSelector);
  const connectionsCRUDActions = useConnectionCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const crmsLeadsSent = useSelector(selectCrmsLeadsSent);
  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const leadsSent =
    (crmsLeadsSent?.jsonWebhookContactsSent || 0) +
    (crmsLeadsSent?.customJsonContactsSent || 0);

  const onNewConnection = async () => {
    const name = autoIncrementedName(
      UNTITLED_CONNECTION_NAME,
      connections.map(c => c.name),
    );

    const connection = await connectionsCRUDActions.create(connectedPageId, {
      name,
      connectionType: 'Webhook',
      description: '',
    });

    if (connection) {
      showSlideoutMenu('editJSONWebhookIntegrationEditor', {
        webhookId: connection.id,
      });
    }
  };

  const sectionProps = {
    landmineName: landmineNames.INTEGRATIONS_JSON_WEBHOOKS,
    featureFlag: 'jsonWebhook',
  };

  const header = (
    <div className="integrations__header-actions">
      <Help
        link="https://customers.ai/help/l/en/article/stqu70gqm1-custom-webhooks"
        text="Help Center"
      />

      {!!connections.length && (
        <div className="integrations__header-status">
          <LinkOutlined className="integrations__header-status-icon" />
          <div className="integrations__header-status-count">
            {connections?.length || 0}
          </div>
          Connections
        </div>
      )}

      {!!leadsSent && (
        <div className="integrations__header-status">
          <TeamOutlined className="integrations__header-status-icon" />
          <div className="integrations__header-status-count">{leadsSent}</div>
          Leads sent
        </div>
      )}

      <PrimaryButton
        className="integrations__header-add-button-secondary"
        onClick={e => {
          e?.stopPropagation();
          onNewConnection();
        }}
        color={ButtonColor.secondaryBlue}
      >
        Add a Connection
      </PrimaryButton>
    </div>
  );

  return (
    <BaseIntegration
      titleComponent={
        <BaseIntegrationHeader
          title="JSON Webhooks "
          subtitle={`Automatically send data from ${companyName} to JSON Webhook`}
          icon={<WebhookIcon color="white" />}
          headerRightComponent={header}
          upgradeButtonName="Add a Connection"
          css={{ icon: 'integrations__webhook-header-icon' }}
          {...sectionProps}
        />
      }
      integrationType="default"
      emptyStateConfig={{
        button: 'Add a JSON Webhooks Connection',
        onClick: onNewConnection,
        icon: <AddWebhookIcon />,
        ...sectionProps,
      }}
      showEmptyState={!connections.length}
    >
      <ConfigProvider
        theme={{
          components: {
            Table: {
              headerBg: 'white',
              borderColor: '#D9D9D9',
              cellPaddingInlineSM: 16,
            },
          },
        }}
      >
        <div className="connections-panel-table">
          <Table
            dataSource={connections}
            columns={cellData}
            pagination={false}
            size="small"
            bordered
          />
        </div>
      </ConfigProvider>
    </BaseIntegration>
  );
};
